// --------------------------------------------------------
// ENCAIXE MODAL - EncaixeModal
// --------------------------------------------------------

// --------------------------------------------------------
// GENERAL
// --------------------------------------------------------
import { Alert, CircularProgress, Modal, Radio, TextField } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components';
import Colors from '../../constants/Colors';
import { cleanMask, getDateDescription, handleBackendErrors } from '../../helpers/HelperFunctions';
import useResponsiveInfo from '../../hooks/useResponsiveInfo'
import CareLineDropdown from '../dropdown/CareLineDropdown';
import { ModalContentContainer } from '../successModal/SuccessModal.styled';
import { GenericSolidButton } from '../UI/buttons/Button.styled';
import { H1, Text } from '../UI/generalLayout/Layout.styled';
import { ReactComponent as Calendar } from '../../assets/imgs/calendarSchedule.svg'
import Category from '../../models/core/careLine/Category';
import InputMask from "react-input-mask";
import { PersonService } from '../../core/http/service/PersonService';
import { useAppDispatch } from '../..';
import ErrorArea from '../error/ErrorArea';
import EncaixeValidacaoModal from '../encaixe/EncaixeValidacaoModal';
import { SchedulingService } from '../../core/http/service/SchedulingService';

// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
// --------------------------------------------------------

// --------------------------------------------------------
// ENCAIXE MODAL INTERFACE
// --------------------------------------------------------

interface IEncaixeModal {
    visible: boolean;
    currentUnitId: string;
    currentDate: string;
    categories: Category[];
    onConfirm: () => void;
    onCancel: () => void;
}
/**
/**
 * A component that renders a encaixe modal.
 *
 * @param props The component props.
 * @returns The component JSX.
 */
export const ModalContainer = styled.div<{ isMobile?: boolean }>`
    border-radius: 8px;
    justify-content: center;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${props => props.isMobile ? '350px' : '800px'};
    height: ${props => props.isMobile ? '550px' : '600px'};
    overflow-y: ${props => props.isMobile ? 'scroll' : ''};
    background-color: ${Colors.SPM600};

    &::-webkit-scrollbar { 
        display: none;
    }
`

export const CancelButton = styled(GenericSolidButton)`
    background-color: white;
    border: 1px solid ${Colors.RED500};
    color: ${Colors.RED500};

    &:active{
        background-color: ${props => props.disabled ? '' : 'white'};
        color: ${props => props.disabled ? '' : `${Colors.RED500}`};
        border-color: ${props => props.disabled ? '' : `${Colors.RED500}`};
    }
`

export const ConfirmButton = styled(GenericSolidButton) <{ disabled: boolean }>`
    background-color: ${Colors.SPM600};
    opacity: ${props => props.disabled ? '0.5' : '1'};
    color: white;

    &:active{
        background-color: ${props => props.disabled ? '' : `${Colors.SPM600}`};
        color: ${props => props.disabled ? '' : `white`};
        border-color: ${props => props.disabled ? '' : ``};
    }
`

export const StyledText = styled(Text)`
    font-size: 14px;
`

export const OverflowAwareContainer = styled(ModalContainer)`
    overflow-y: scroll;
    &::-webkit-scrollbar { 
        display: inline;
    }
`

const EncaixeModal = ({ visible, currentDate, categories, currentUnitId, onConfirm, onCancel }: IEncaixeModal) => {
    // --------------------------------------------------------
    // ENCAIXE MODAL PRIVATE DECLARATIONS
    // --------------------------------------------------------
    const [appointmentTypes] = useState([{ nome: 'Atendimento Clínico', id: 'Clinica' }, { nome: 'Atendimento Periódico', id: 'Periodica' }, { nome: 'Exame Preventivo', id: 'Preventiva' }])
    const [selectedAppointmentType, setSelectedAppointmentType] = useState<any>({ nome: '', id: '' })
    const [selectedCategory, setSelectedCategory] = useState<Category>(new Category());
    const [selectedHealthProfessional, setSelectedHealthProfessional] = useState<any>({ id: '', nome: '' })
    const [pessoasByCpfFilter, setPessoasByCpfFilter] = useState<any[]>([])
    const [healthProfessionalsFilter, setHealthProfessionalsFilter] = useState<any[]>([])
    const [pessoaSelected, setPessoaSelected] = useState<any>({ id: '', nome: '', unidadeAtendimentoPermitida: true })
    const [isLoadingFetchPessoa, setIsLoadingFetchPessoa] = useState(false)
    const [cpf, setCpf] = useState('')
    const [isHorarioValido, setHorarioValido] = useState<boolean>(false)
    const [horario, setHorario] = useState('')
    const [errors, setErrors] = useState<any[]>([])
    const [isAnyRequiredInfoEmpty, setIsAnyRequiredInfoEmpty] = useState(false)
    const [futuroAgendamentoEncaixe, setFuturoAgendamentoEncaixe] = useState<any>(null);
    const [validarEncaixeModalVisible, setValidarEncaixeModalVisible] = useState<boolean>(false);
    const { isMobile } = useResponsiveInfo();
    const personService: PersonService = new PersonService();
    const dispatch = useAppDispatch()
    const isUnitNotAllowed = !pessoaSelected.unidadeAtendimentoPermitida
    const confirmButtonDisabled = !isHorarioValido || isAnyRequiredInfoEmpty;


    const fetchPessoasByCpf = useCallback(async () => {
        setIsLoadingFetchPessoa(true)
        let peopleByCpf: any[] = [];
        try {
            const processResponse = (res: any) => {
                const list: any[] = [];
                res.data.data.forEach((data: any) => {
                    data.empresas.forEach((empresa: any) => {
                        const object =
                        {
                            nomePessoa: data.nome,
                            nome: `${data.nome} - ${empresa.nome}`,
                            pessoaId: data.id,
                            id: empresa.id,
                            nomeEmpresa: empresa.nome,
                            unidadeAtendimentoPermitida: empresa.unidadeAtendimentoPermitida
                        }
                        list.push(object);
                    })
                })
                return list;
            }

            // 1. Tenta buscar titular
            const response: any = await personService.getPessoaEmpresaByCPF(cleanMask(cpf), currentUnitId);
            peopleByCpf = processResponse(response);

            // 2. Se não encontrou titular, busca dependente no novo endpoint
            if (peopleByCpf.length === 0) {
                const depResponse: any = await personService.getDependenteByCPF(cleanMask(cpf), currentUnitId);
                peopleByCpf = processResponse(depResponse);
            }

            setPessoasByCpfFilter(peopleByCpf)
            if (peopleByCpf.length === 1) {
                setPessoaSelected(peopleByCpf[0]);
            } else {
                setPessoaSelected({ id: '', nome: '', unidadeAtendimentoPermitida: true });
            }
        } catch (error: any) {
            setPessoasByCpfFilter([])
        }
        setIsLoadingFetchPessoa(false)
    }, [cpf, currentUnitId])

    const fetchHealthProfessionals = useCallback(async () => {
        const healthProfessionals: any[] = [];
        try {
            const [day, month, year] = currentDate.split('/')
            const date = `${year}-${month}-${Number(day) <= 9 ? '0' + day : day}`

            const response: any = await personService.getProfessionalsByCategory(selectedCategory.id ?? '', currentUnitId, date)
            response.data.data.forEach((p: any) => {
                healthProfessionals.push(p)
            })

            if (healthProfessionals.length === 0) {
                setSelectedHealthProfessional({ id: '', nome: '' })
            }

            setHealthProfessionalsFilter(healthProfessionals)

        } catch (error: any) {
            setHealthProfessionalsFilter([])
        }
    }, [selectedCategory])

    useEffect(() => {
        if (selectedCategory.id !== '') {
            fetchHealthProfessionals();
        }
    }, [selectedCategory])

    useEffect(() => {
        if (cleanMask(cpf).length === 11) {
            fetchPessoasByCpf();
        }
    }, [cpf])

    useEffect(() => {
        if (horario.match(new RegExp(/^(?:\d|[01]\d|2[0-3]):[0-5]\d$/gm))) {
            setHorarioValido(true)
        } else {
            setHorarioValido(false)
        }
    }, [horario])

    useEffect(() => {
        const isCategoryEmpty = selectedCategory.id === '';
        const isPessoaEmpty = pessoaSelected.id === '';
        const isHealthProfessionalEmpty = selectedHealthProfessional.id === '';
        const isAppointmentTypeEmpty = selectedAppointmentType.id === '';

        if (isCategoryEmpty || isPessoaEmpty || isHealthProfessionalEmpty || isAppointmentTypeEmpty) {
            setIsAnyRequiredInfoEmpty(true)
        } else {
            setIsAnyRequiredInfoEmpty(false)
        }

    }, [selectedCategory, pessoaSelected, selectedHealthProfessional, selectedAppointmentType])

    const cleanModalInfo = () => {
        setSelectedCategory(new Category());
        setFuturoAgendamentoEncaixe(null)
        setValidarEncaixeModalVisible(false)
        setSelectedAppointmentType({ nome: '', id: '' })
        setSelectedHealthProfessional({ nome: '', id: '' })
        setPessoaSelected({ id: '', nome: '', unidadeAtendimentoPermitida: true })
        setCpf('')
        setHorario('')
    }

    const onCloseModal = () => {
        cleanModalInfo();
        onCancel();
    }

    const onConfirmClick = async () => {
        try {
            const service: SchedulingService<any> = new SchedulingService();
            const [day, month, year] = currentDate.split('/')
            const date = `${year}-${Number(month) <= 9 ? '0' + month : month}-${Number(day) <= 9 ? '0' + day : day}T${horario}`
            const pacienteId = pessoaSelected.pessoaId;
            const empresaId = pessoaSelected.id;
            const profissionalId = selectedHealthProfessional.id;
            const tipoConsulta = selectedAppointmentType.id;
            const unidade = currentUnitId;

            const params = {
                pacienteId: pacienteId,
                empresaId: empresaId,
                tipoConsulta: tipoConsulta,
                unidadeAtendimentoId: unidade,
                profissionalId: profissionalId,
                horario: date
            }

            const result: any = await service.postEncaixe(params)
            if (result.data.data.agendamentoFuturo !== null) {
                const futuroAgendamento: any = result.data.data.agendamentoFuturo;
                setFuturoAgendamentoEncaixe(futuroAgendamento)
                setValidarEncaixeModalVisible(true)
            } else {
                cleanModalInfo();
                onConfirm();
            }
        } catch (error: any) {
            handleBackendErrors(error, setErrors)
        }
    }

    // --------------------------------------------------------

    // --------------------------------------------------------
    // REACT RETURN FUNCTION
    // --------------------------------------------------------
    return (
        <>
            <Modal
                open={visible}
            >
                <OverflowAwareContainer isMobile={isMobile} style={{ backgroundColor: 'white', height: isUnitNotAllowed ? "600px" : "550px" }}>
                    <ModalContentContainer isMobile={isMobile}>
                        <div style={{ textAlign: 'center' }}>
                            <H1>Adicionar encaixe</H1>
                            <Text>Preencha os dados da consulta do paciente e confirme</Text>
                        </div>
                        <ErrorArea errorList={errors} onCloseAction={() => { setErrors([]) }} />
                        {isUnitNotAllowed && (<Alert severity="warning" variant="outlined">Empresa do paciente não parametrizada para atendimento nessa unidade</Alert>)}

                        <StyledText>CPF do Paciente*:</StyledText>
                        <div style={{ flexDirection: isMobile ? 'column' : 'row', display: 'flex' }}>
                            <InputMask
                                mask="999.999.999-99"
                                onKeyPress={e => { e.key === 'Enter' && e.preventDefault() }}
                                maskPlaceholder="" value={cpf}
                                onChange={(event: any) => { setCpf(event.target.value) }}
                            >
                                <TextField error={false} placeholder="000.000.000-00" style={{ marginRight: 10, width: isMobile ? '100%' : 300 }} size="small" />
                            </InputMask>
                            {cleanMask(cpf).length === 11 && (
                                <>
                                    {!isLoadingFetchPessoa ? (
                                        <>
                                            {pessoasByCpfFilter.length > 0 && (<CareLineDropdown
                                                value={pessoaSelected}
                                                onChange={setPessoaSelected}
                                                items={pessoasByCpfFilter}
                                                containerStyle={{ width: isMobile ? '100%' : '', marginTop: isMobile ? 5 : 0 }}
                                                style={{ width: isMobile ? '100%' : 350, height: 42 }}
                                                disabled={false}
                                            />)}
                                        </>) : (<CircularProgress />)}
                                </>)}
                        </div>

                        <StyledText>
                            Tipo de Atendimento:
                        </StyledText>
                        <div style={{ flexDirection: isMobile ? 'column' : 'row', display: 'flex', justifyContent: 'center' }}>
                            {appointmentTypes.map(appointmentType => {
                                return <div style={{ flexDirection: 'row', display: 'flex', marginTop: -10, marginBottom: -10 }}>
                                    <Radio
                                        checked={appointmentType.id === selectedAppointmentType.id}
                                        onChange={() => { setSelectedAppointmentType(appointmentType) }}
                                        value={selectedAppointmentType}
                                    />
                                    <StyledText>{appointmentType.nome}</StyledText>
                                </div>
                            })}
                        </div>
                        <div style={{ flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', width: '100%', display: 'flex' }}>
                            <div style={{ width: isMobile ? '100%' : '50%' }}>
                                <StyledText>Data:</StyledText>
                                <div style={{ flexDirection: 'row', display: 'flex', }}>
                                    <Calendar style={{ marginTop: 0 }} />
                                    <StyledText style={{ padding: 5, marginTop: -1 }}>{getDateDescription(currentDate)}</StyledText>
                                </div>
                                <div>
                                    <StyledText>Tipo do Profissional*:</StyledText>
                                    <CareLineDropdown
                                        value={selectedCategory}
                                        onChange={setSelectedCategory}
                                        items={categories}
                                        containerStyle={{ width: isMobile ? '100%' : '' }}
                                        style={{ width: isMobile ? '100%' : 350, height: 42 }}
                                        disabled={false}
                                    />
                                </div>

                            </div>
                            <div style={{ width: isMobile ? '100%' : '50%' }}>
                                <StyledText>Horário*:</StyledText>
                                <InputMask
                                    mask="99:99"
                                    onKeyPress={e => { e.key === 'Enter' && e.preventDefault() }}
                                    maskPlaceholder="" value={horario}
                                    onChange={(event: any) => { setHorario(event.target.value) }}
                                >
                                    <TextField error={false} placeholder="00:00" style={{ marginRight: 10, width: 80 }} size="small" />
                                </InputMask>
                                <StyledText>Profissional*:</StyledText>
                                <CareLineDropdown
                                    value={selectedHealthProfessional}
                                    onChange={setSelectedHealthProfessional}
                                    items={healthProfessionalsFilter}
                                    containerStyle={{ width: isMobile ? '100%' : '' }}
                                    style={{ width: isMobile ? '100%' : 350, height: 42, opacity: selectedCategory.id === null || healthProfessionalsFilter.length === 0 ? 0.5 : 1 }}
                                    disabled={selectedCategory.id === null || healthProfessionalsFilter.length === 0}
                                />
                            </div>
                        </div>
                        <div style={{ flexDirection: isMobile ? 'column' : 'row', marginBottom: isMobile ? 30 : 0, justifyContent: 'center', marginTop: 25, display: 'flex' }}>
                            <CancelButton style={{ marginRight: 10, marginBottom: isMobile ? 10 : 0 }} onClick={onCloseModal}>Cancelar</CancelButton>
                            <ConfirmButton disabled={confirmButtonDisabled} onClick={onConfirmClick}>Confirmar</ConfirmButton>
                        </div>
                    </ModalContentContainer>
                    <EncaixeValidacaoModal
                        agendamentoFuturo={futuroAgendamentoEncaixe}
                        visible={validarEncaixeModalVisible}
                        onDesmarcarEncaixe={() => { cleanModalInfo(); onConfirm(); }}
                        onEncaixe={() => { cleanModalInfo(); onConfirm(); }}
                    />
                </OverflowAwareContainer>
            </Modal>
        </>
    )
    // --------------------------------------------------------
}

export default EncaixeModal